import { useState, useEffect, createContext, useContext } from 'react';
import { X, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Context for global modal control
const ModalContext = createContext(null);

export const useCounsellingModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useCounsellingModal must be used within CounsellingProvider');
  return ctx;
};

export const CounsellingProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [presetData, setPresetData] = useState({});

  const open = (preset = {}) => {
    setPresetData(preset);
    setIsOpen(true);
  };
  const close = () => {
    setIsOpen(false);
    setPresetData({});
  };

  return (
    <ModalContext.Provider value={{ open, close, isOpen }}>
      {children}
      {isOpen && <CounsellingModal onClose={close} presetData={presetData} />}
    </ModalContext.Provider>
  );
};

export const DEGREE_LEVELS = ["Bachelor's", "Master's", 'PhD'];

export const COURSES = [
  'MBBS',
  'Engineering',
  'Computer Science',
  'Artificial Intelligence',
  'Data Science',
  'Pharmacy',
  'Biotechnology',
  'Business Management',
  'Economics',
  'Psychology',
  'Architecture',
  'Law',
  'Other',
];

export const COUNTRIES = [
  'Italy',
  'Russia',
  'Georgia',
  'Kazakhstan',
  'Kyrgyzstan',
  'Uzbekistan',
  'Nepal',
  'Other',
];

const SectionHeader = ({ children }) => (
  <div style={{
    fontSize: '11px',
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: 'var(--gold)',
    fontWeight: 600,
    margin: '8px 0 -4px 0',
    paddingTop: '12px',
    borderTop: '1px solid var(--border-subtle)',
  }}>
    {children}
  </div>
);

const CounsellingModal = ({ onClose, presetData = {} }) => {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    degree_level: presetData.degree_level || '',
    course: presetData.course || '',
    country: presetData.country || 'Italy',
    message: presetData.message || '',
    source: presetData.source || 'website',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && !submitting && onClose();
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose, submitting]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/counselling`, form);
      toast.success('Enquiry submitted! Check your email for confirmation.');
      setForm({
        full_name: '', email: '', phone: '', city: '',
        degree_level: '', course: '', country: 'Italy', message: '', source: 'website',
      });
      setTimeout(onClose, 800);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !submitting && onClose()} data-testid="counselling-modal">
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="modal-close-btn" aria-label="Close" disabled={submitting}>
          <X size={20} />
        </button>
        <div className="modal-header">
          <div className="modal-eyebrow">Free Consultation</div>
          <h3 className="modal-title">Book Your Counselling</h3>
          <p className="modal-subtitle">Speak to an Italy education expert — guidance from a University of Turin PhD graduate.</p>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="form-group" data-testid="counselling-form">

            <SectionHeader>Personal Information</SectionHeader>
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">Full Name *</label>
                <input
                  type="text" name="full_name" required
                  value={form.full_name} onChange={handleChange}
                  className="form-input" placeholder="Your name"
                  data-testid="modal-name-input"
                />
              </div>
              <div className="form-field">
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel" name="phone" required
                  value={form.phone} onChange={handleChange}
                  className="form-input" placeholder="+91 98xxxxxxxx"
                  data-testid="modal-phone-input"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">Email Address *</label>
                <input
                  type="email" name="email" required
                  value={form.email} onChange={handleChange}
                  className="form-input" placeholder="you@email.com"
                  data-testid="modal-email-input"
                />
              </div>
              <div className="form-field">
                <label className="form-label">City *</label>
                <input
                  type="text" name="city" required
                  value={form.city} onChange={handleChange}
                  className="form-input" placeholder="Your city"
                  data-testid="modal-city-input"
                />
              </div>
            </div>

            <SectionHeader>Academic Information</SectionHeader>
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">Degree Level *</label>
                <select
                  name="degree_level" required
                  value={form.degree_level} onChange={handleChange}
                  className="form-select"
                  data-testid="modal-degree-select"
                >
                  <option value="">Select degree</option>
                  {DEGREE_LEVELS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">Course Interested *</label>
                <select
                  name="course" required
                  value={form.course} onChange={handleChange}
                  className="form-select"
                  data-testid="modal-course-select"
                >
                  <option value="">Select course</option>
                  {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <SectionHeader>Destination</SectionHeader>
            <div className="form-field">
              <label className="form-label">Country Interested *</label>
              <select
                name="country" required
                value={form.country} onChange={handleChange}
                className="form-select"
                data-testid="modal-country-select"
              >
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <SectionHeader>Additional Information</SectionHeader>
            <div className="form-field">
              <label className="form-label">Message (Optional)</label>
              <textarea
                name="message" rows="3"
                value={form.message} onChange={handleChange}
                className="form-textarea"
                placeholder="Tell us about your background and goals..."
                data-testid="modal-message-textarea"
              />
            </div>

            <button
              type="submit" className="form-submit" disabled={submitting}
              data-testid="modal-submit-btn"
            >
              {submitting ? 'Submitting...' : (<>Submit Enquiry <Send size={14} /></>)}
            </button>
            <p className="form-note">🔒 Your data is private. We will respond within 24-48 hours.</p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CounsellingModal;
